import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { getToken, clearToken, isAuthenticated } from '@/lib/auth';
import {
  LayoutGrid, FileText, BookOpen, Package,
  Users, Settings, Menu, LogOut, Mail, ShoppingCart, MessageSquare, History, Cog, Image, Link2
} from 'lucide-react';

const ADMIN_LINKS = [
  { href: '/admin', label: 'Dashboard', icon: LayoutGrid },
  { href: '/admin/pages', label: 'Pages', icon: Settings },
  { href: '/admin/essays', label: 'Essays', icon: FileText },
  { href: '/admin/frameworks', label: 'Frameworks', icon: BookOpen },
  { href: '/admin/media', label: 'Media Library', icon: Image },
  { href: '/admin/links', label: 'Links Page', icon: Link2 },
  { href: '/admin/products', label: 'Products', icon: Package },
  { href: '/admin/waitlist', label: 'Waitlist & Newsletter', icon: Users },
  { href: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { href: '/admin/contacts', label: 'Messages', icon: MessageSquare },
  { href: '/admin/audit-log', label: 'Audit Log', icon: History },
  { href: '/admin/settings', label: 'Settings', icon: Cog },
];

export const AdminShell = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    clearToken();
    navigate('/admin/login');
  };

  const SidebarContent = ({ onLinkClick }) => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-[rgba(201,168,76,0.12)]">
        <Link
          to="/"
          className="font-['Cormorant_Garamond'] text-base font-bold text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))] transition-colors"
        >
          THE COSMIC SYNTHESIST
        </Link>
        <p className="font-['Inter'] text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--silver))] mt-1">
          Admin Portal
        </p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {ADMIN_LINKS.map(link => {
          const Icon = link.icon;
          const isActive = location.pathname === link.href;
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={onLinkClick}
              data-testid={`admin-sidebar-${link.label.toLowerCase().replace(/\s+&?\s*/g, '-')}-link`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-['Inter'] transition-colors duration-200 ${
                isActive
                  ? 'bg-[rgba(27,77,68,0.3)] text-[hsl(var(--gold))] border border-[rgba(201,168,76,0.2)]'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(168,181,178,0.06)]'
              }`}
            >
              <Icon className="h-4 w-4" />
              {link.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[rgba(201,168,76,0.08)]">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-['Inter'] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(168,181,178,0.06)] transition-colors w-full"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[hsl(164,28%,14%)]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 admin-sidebar fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 sm:px-6 border-b border-[rgba(201,168,76,0.08)] backdrop-blur-md" style={{ background: 'rgba(7,10,10,0.75)' }}>
          <div className="flex items-center gap-4">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5 text-[hsl(var(--silver))]" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 p-0 admin-sidebar border-r border-[rgba(201,168,76,0.12)]"
              >
                <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-bold text-[hsl(var(--foreground))]">
              {title}
            </h1>
          </div>
          <Link
            to="/"
            className="font-['Inter'] text-xs text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            View Site
          </Link>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto blueprint-grid min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
};
